import { useState } from 'react';
import {
  Server,
  Send,
  CheckCircle2,
  Package,
  Terminal,
  Shield,
  Copy,
  Check,
} from 'lucide-react';

import {
  ActionIcon,
  Box,
  Button,
  Container,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Code,
  List,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

export default function DockerLicense() {
  const { t } = useTranslation();

  const dockerComposeYaml = `# docker-compose.yml
version: '3.8'

services:
  timestampgit:
    image: rue1401/timestampgit:prod
    container_name: timestampgit
    ports:
      - "8080:8080"
    volumes:
      - ./data:/app/data
    environment:
      - TZ=UTC
    restart: unless-stopped

  valkey:
    image: valkey/valkey:8
    container_name: timestampgit-valkey
    volumes:
      - ./valkey-data:/data
    restart: unless-stopped`;

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const rsp = await fetch('/api/docker-license', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, company, email, phone }),
      });

      if (!rsp.ok) {
        const text = await rsp.text();
        throw new Error(text || 'Request failed');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="md">
        <Stack gap="xl">
          <Stack gap={4}>
            <Title order={1}>{t('Docker Self-Hosted License')}</Title>
            <Text c="dimmed" size="sm">
              {t('Deploy Timestamp GIT on your own infrastructure for maximum security and air-gapped environments.')}
            </Text>
          </Stack>

          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Group gap="xs">
                <ThemeIcon variant="light" color="teal" radius="xl" size="sm">
                  <Package size={14} />
                </ThemeIcon>
                <Text fw={600}>{t('Docker Compose Installation')}</Text>
              </Group>

              <Text size="sm">
                {t('Timestamp GIT Enterprise is distributed as a Docker image. Use the following Docker Compose configuration to get started:')}
              </Text>

              <Box pos="relative">
                <CopyButton value={dockerComposeYaml}>
                  {({ copied, copy }) => (
                    <ActionIcon
                      variant="subtle"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                      pos="absolute"
                      top={8}
                      right={8}
                      style={{ zIndex: 1 }}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </ActionIcon>
                  )}
                </CopyButton>
                <Code block>
{dockerComposeYaml}
                </Code>
              </Box>

              <Text size="sm" fw={600}>{t('Quick Start')}</Text>
              <Code block>
{`# Pull the image
docker compose pull

# Start the services
docker compose up -d

# Check logs
docker compose logs -f timestampgit`}
              </Code>

              <Text size="sm">
                {t('The application will be available at http://localhost:8080 after startup. On first launch, follow the setup wizard to connect your GitHub App and configure your instance.')}
              </Text>
            </Stack>
          </Paper>

          {submitted ? (
            <Paper withBorder radius="md" p="xl">
              <Stack align="center" gap="md">
                <ThemeIcon variant="light" color="teal" radius="xl" size={48}>
                  <CheckCircle2 size={24} />
                </ThemeIcon>
                <Title order={3}>{t('Request Submitted')}</Title>
                <Text c="dimmed" size="sm" ta="center" maw={400}>
                  {t('Thank you for your interest. We will review your request and send a demo license file to your email address within one business day.')}
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Paper withBorder radius="md" p="lg">
              <Stack gap="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="blue" radius="xl" size="sm">
                    <Shield size={14} />
                  </ThemeIcon>
                  <Text fw={600}>{t('Request a Demo License')}</Text>
                </Group>

                <Text size="sm" c="dimmed">
                  {t('Fill out the form below to receive a time-limited demo license for evaluation.')}
                </Text>

                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      required
                      label={t('Name')}
                      placeholder={t('Your full name')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <TextInput
                      required
                      label={t('Company')}
                      placeholder={t('Your company or organization')}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                    <TextInput
                      required
                      type="email"
                      label={t('Email')}
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextInput
                      label={t('Phone')}
                      placeholder={t('Optional contact number')}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />

                    {error && (
                      <Text c="red" size="sm">{error}</Text>
                    )}

                    <Button
                      type="submit"
                      loading={submitting}
                      leftSection={<Send size={14} />}
                    >
                      {t('Submit Request')}
                    </Button>
                  </Stack>
                </form>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
